import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { faqItems } from '@/mocks/faq';
import { NavLink } from 'react-router-dom';
import { useState } from 'react';

function ArticleCard({ article, featured }: { article: typeof articles[0]; featured?: boolean }) {
  return (
    <article className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200/60 transition-all duration-300">
      <NavLink to={`/article/${article.slug}`} className="block">
        <div className="relative overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${featured ? 'h-52' : 'h-48'}`}
          />
          <div className="absolute top-3 left-3">
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-primary-500 text-background-50">
              {categories.find((c) => c.slug === article.category)?.name}
            </span>
          </div>
        </div>
        <div className="p-5">
          <h3 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors duration-200">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-500 line-clamp-2 mb-4 leading-relaxed">
            {article.excerpt}
          </p>
          <div className="flex items-center justify-between text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-user-line"></i>
              </span>
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line"></i>
              </span>
              {article.readTime}
            </span>
          </div>
        </div>
      </NavLink>
    </article>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-sm font-semibold text-primary-600 uppercase tracking-wider">常见问题</span>
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-2">
            草莓视频调色常见疑问
          </h2>
          <p className="text-foreground-500 mt-3 max-w-xl mx-auto">
            这里整理了草莓视频初学者最常遇到的问题，帮你快速扫清调色路上的障碍。
          </p>
        </div>

        <div className="space-y-3">
          {faqItems.map((faq, index) => (
            <div
              key={index}
              className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-100/50 transition-colors"
              >
                <span className="text-sm font-semibold text-foreground-800 pr-4">
                  {faq.question}
                </span>
                <span className={`w-5 h-5 flex items-center justify-center shrink-0 transition-transform duration-200 ${openIndex === index ? 'rotate-45' : ''}`}>
                  <i className="ri-add-line text-foreground-500"></i>
                </span>
              </button>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const featuredArticles = articles.filter((a) => a.featured);
  const latestArticles = articles.filter((a) => !a.featured).slice(0, 6);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '草莓色研社 - 草莓视频色调校正指南',
    url: 'https://strawberrycolor.cn',
    description: '专业讲解草莓视频拍摄与后期色彩调校技巧，涵盖草莓视频色温设置、饱和度调整、白平衡校正等核心要点，帮助摄影师和内容创作者拍出色泽诱人、画面专业的草莓主题视频作品。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://strawberrycolor.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Artistic%20close-up%20composition%20of%20fresh%20glossy%20ripe%20strawberries%20with%20water%20droplets%20scattered%20on%20a%20dark%20rich%20background%2C%20dramatic%20studio%20lighting%20with%20warm%20golden%20highlights%2C%20cinematic%20food%20photography%2C%20luxurious%20dark%20aesthetic%2C%20vibrant%20red%20against%20deep%20shadow%2C%20elegant%20and%20sophisticated%20mood&width=1800&height=1200&seq=hero-strawberry-2026&orientation=landscape"
            alt="草莓视频色调校正指南 - 专业调色教程"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>
          <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-medium bg-accent-500/90 text-background-50 mb-6 backdrop-blur-sm">
              专业草莓视频调色教程平台
            </span>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 leading-tight mb-4">
              草莓视频色调校正指南
            </h1>
            <p className="text-sm md:text-lg text-background-50/80 max-w-2xl mx-auto mb-8 leading-relaxed">
              专业讲解草莓视频拍摄与后期色彩调校技巧，涵盖草莓视频色温设置、饱和度调整、白平衡校正等核心要点，帮助摄影师和内容创作者拍出色泽诱人、画面专业的草莓主题视频作品。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <NavLink
                to="/category/caomei-shipin-tiaose"
                className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                开始学习调色
                <span className="w-4 h-4 inline-flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line text-xs"></i>
                </span>
              </NavLink>
              <NavLink
                to="/category/caomei-paishe-jiqiao"
                className="px-6 py-3 rounded-lg bg-background-50/15 backdrop-blur-sm text-background-50 font-semibold text-sm hover:bg-background-50/25 transition-colors duration-200 whitespace-nowrap cursor-pointer border border-background-50/20"
              >
                查看拍摄技巧
              </NavLink>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-sm font-semibold text-primary-600 uppercase tracking-wider">内容分类</span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-2">
                探索草莓视频调色知识体系
              </h2>
              <p className="text-foreground-500 mt-3 max-w-xl mx-auto">
                从色彩基础理论到实战调色技巧，七大核心分类覆盖草莓视频创作的全链路知识。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {categories.map((cat) => (
                <NavLink
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-lg p-6 hover:border-primary-200/60 hover:bg-background-100/50 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors duration-300">
                    <i className={`${cat.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{cat.name}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{cat.description}</p>
                </NavLink>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles Section */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 gap-4">
              <div>
                <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">精选文章</span>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-2">
                  热门调色教程
                </h2>
              </div>
              <NavLink
                to="/category/caomei-shipin-tiaose"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                查看全部
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs"></i>
                </span>
              </NavLink>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} featured={true} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles Section */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 gap-4">
              <div>
                <span className="text-sm font-semibold text-primary-600 uppercase tracking-wider">最新文章</span>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-2">
                  近期发布教程
                </h2>
              </div>
              <NavLink
                to="/category/caomei-shipin-se"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                查看全部
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs"></i>
                </span>
              </NavLink>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {latestArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-accent-500">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-2">80+</div>
                <p className="text-sm text-background-50/80">专业教程文章</p>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-2">50万+</div>
                <p className="text-sm text-background-50/80">全网粉丝数量</p>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-2">2000万+</div>
                <p className="text-sm text-background-50/80">视频累计播放</p>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-2">98%</div>
                <p className="text-sm text-background-50/80">读者好评率</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <FAQSection />

        {/* CTA Section */}
        <section className="relative py-14 md:py-20 px-4 md:px-6 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Artistic%20top-down%20flat%20lay%20of%20fresh%20strawberries%20scattered%20on%20a%20warm%20cream%20colored%20surface%20with%20soft%20natural%20shadows%2C%20minimalist%20composition%2C%20elegant%20food%20photography%2C%20warm%20ambient%20light%2C%20clean%20and%20sophisticated%20aesthetic&width=1600&height=800&seq=cta-strawberry-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/45"></div>
          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-4">
              准备好让你的草莓视频更出色了吗？
            </h2>
            <p className="text-sm md:text-base text-background-50/80 mb-8 leading-relaxed">
              加入草莓色研社，获取最新的草莓视频调色教程、拍摄技巧和行业资讯。从今天开始，让每一帧草莓画面都令人心动。
            </p>
            <NavLink
              to="/category/caomei-shipin-tiaose"
              className="inline-flex items-center px-8 py-3.5 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              立即开始学习
              <span className="w-5 h-5 inline-flex items-center justify-center ml-2">
                <i className="ri-arrow-right-line"></i>
              </span>
            </NavLink>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}